import type { MutateSelectedFailure } from '../../hooks/useBillingCycles';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

type ShowErrorFn = (title: string, message: string) => void;

const CREATE_CLAIM_FALLBACK = 'Failed to create claim';

/** Cap the per-claim lines so a large batch failure stays a readable toast. */
const MAX_DETAILED_LINES = 5;

/**
 * Toast the per-cycle reasons a Create Claim batch failed.
 *
 * Unlike the send flow (whose 4xx details already name the patient), the
 * create-claim 400s describe only the chart problem — e.g. "Primary diagnosis
 * R69 cannot be grouped: …" or "No eligible payment OASIS found for billing
 * cycle" — so every line leads with the patient so the biller knows which row
 * to fix. Only 4xx business details are surfaced (via getBusinessErrorMessage);
 * anything else falls back to generic copy with the MRN for identification.
 */
export const showCreateClaimFailures = (
  failures: MutateSelectedFailure[],
  showError: ShowErrorFn
) => {
  const lines = failures.slice(0, MAX_DETAILED_LINES).map(failure => {
    const reason =
      getBusinessErrorMessage(failure.error) ?? CREATE_CLAIM_FALLBACK;
    const identity =
      reason === CREATE_CLAIM_FALLBACK && failure.mrn
        ? `${failure.name} (MRN ${failure.mrn})`
        : failure.name;
    return `• ${identity}: ${reason}`;
  });
  const remaining = failures.length - MAX_DETAILED_LINES;
  if (remaining > 0) {
    lines.push(`• …and ${remaining} more`);
  }

  showError('Some claims could not be created', lines.join('\n'));
};
